/**
 * Shared primitives.
 *
 * Every rules entity is referenced by a stable, English, locale-independent id
 * (`spell:fire_bolt`, `creature:goblin_warrior`, `condition:prone`, `skill:stealth`,
 * `coin:sp`). Display text is a `LocalizedString`. Distances are feet in multiples
 * of five, weights are pounds, value is copper — the render layer converts
 * (architecture §5.2, §9.3).
 */
import { z } from 'zod';
/* ------------------------------------------------------------------ locale */
export const Locale = z.enum(['en-US', 'pt-BR']);
/** Every player-visible pack string carries both locales; `key-parity` lints it. */
export const LocalizedString = z.object({
    'en-US': z.string(),
    'pt-BR': z.string(),
});
/* -------------------------------------------------------------------- refs */
/** A slug segment used inside ids: lowercase, digits and underscores. */
export const Slug = z.string().regex(/^[a-z0-9_]+$/);
/**
 * A scene/board token target: the player, a real living-world NPC, or an
 * anonymous stat block (architecture §6.2).
 */
export const Ref = z.string().regex(/^(player|npc:[a-z0-9_]+|stat:[a-z0-9_]+)$/);
/** An entity reference in the wider world graph (`npc:`, `loc:`, `item:`, `faction:`, `quest:`). */
export const EntityRef = z
    .string()
    .regex(/^(player|npc|loc|item|faction|quest|storylet|prop|effect|encounter|srd):[a-z0-9_.]+$/);
/**
 * The actor column of the event log (architecture §8.3):
 * `player:<id> | npc:<id> | gm | engine | director | governor | system`.
 */
export const ActorId = z.string().min(1);
/* ------------------------------------------------------------- rules atoms */
export const AbilityId = z.enum(['str', 'dex', 'con', 'int', 'wis', 'cha']);
export const SkillId = z.enum([
    'athletics',
    'acrobatics',
    'sleight_of_hand',
    'stealth',
    'arcana',
    'history',
    'investigation',
    'nature',
    'religion',
    'animal_handling',
    'insight',
    'medicine',
    'perception',
    'survival',
    'deception',
    'intimidation',
    'performance',
    'persuasion',
]);
/** The 18 skills plus the 6 abilities — the `request_check.key` domain (§3.3). */
export const CheckKey = z.enum([...SkillId.options, ...AbilityId.options]);
export const ProficiencyLevel = z.enum(['none', 'half', 'proficient', 'expertise']);
export const Size = z.enum(['tiny', 'small', 'medium', 'large', 'huge', 'gargantuan']);
export const Alignment = z.enum([
    'LG',
    'NG',
    'CG',
    'LN',
    'N',
    'CN',
    'LE',
    'NE',
    'CE',
    'unaligned',
]);
export const DamageType = z.enum([
    'acid',
    'bludgeoning',
    'cold',
    'fire',
    'force',
    'lightning',
    'necrotic',
    'piercing',
    'poison',
    'psychic',
    'radiant',
    'slashing',
    'thunder',
]);
/** The 15 SRD 5.2.1 conditions. Exhaustion is also a level 0..6 on the actor. */
export const ConditionId = z.enum([
    'blinded',
    'charmed',
    'deafened',
    'exhaustion',
    'frightened',
    'grappled',
    'incapacitated',
    'invisible',
    'paralyzed',
    'petrified',
    'poisoned',
    'prone',
    'restrained',
    'stunned',
    'unconscious',
]);
/**
 * ADR-095: what a spell does to a creature that the SRD's condition list does not name.
 * `bane`: 1d4 off its attack rolls and saves. `enfeebled` (Ray of Enfeeblement, failed):
 * disadvantage on Strength-based tests and 1d8 off its damage. `unsteady` (Ray of
 * Enfeeblement, made): disadvantage on its next attack.
 */
export const SpellAffliction = z.enum(['bane', 'enfeebled', 'unsteady']);
export const Recharge = z.enum(['turn', 'shortRest', 'longRest', 'dawn', 'dusk', 'manual']);
/** `"1d8+3"`, `"2d6"`, `"7"`. */
export const DiceExpr = z.string().regex(/^(\d+d\d+|\d+)([+-](\d+d\d+|\d+))*$/);
export const Provenance = z.object({
    source: z.string(),
    label: z.string().optional(),
});
export const Modifier = Provenance.extend({ value: z.number().int() });
/**
 * A `parts[]` entry as the HUD, the Ledger and the dice tray render it:
 * "CA 18 = cota de malha 16 + escudo 2" (architecture §1.4, §5.2).
 */
export const RollPart = z.object({
    src: z.string(),
    v: z.number().int(),
    label: z.string(),
});
export const Advantage = z.enum(['none', 'advantage', 'disadvantage']);
/** The engine's short form, used inside `RollRequested` payloads. */
export const AdvantageState = z.enum(['none', 'adv', 'dis']);
/** DC ladder: trivial 5 · easy 10 · moderate 15 · hard 20 · very_hard 25 · nearly_impossible 30. */
export const DifficultyTier = z.enum([
    'trivial',
    'easy',
    'moderate',
    'hard',
    'very_hard',
    'nearly_impossible',
]);
export const DIFFICULTY_DC = Object.freeze({
    trivial: 5,
    easy: 10,
    moderate: 15,
    hard: 20,
    very_hard: 25,
    nearly_impossible: 30,
});
export const Cover = z.enum(['none', 'half', 'three_quarters', 'total']);
/** Every roll kind the engine emits; `rollKinds` says which may be I-roll (§5.4). */
export const RollKind = z.enum([
    'attack',
    'check',
    'save',
    'death_save',
    'damage',
    'initiative',
    'concentration',
]);
/** Auto = the engine rolls; player = the dice tray, the `AwaitingRoll` sub-state. */
export const RollMode = z.enum(['auto', 'player']);
/**
 * Shipped defaults (ADR-122, 2026-09-24): every roll that belongs to the player is thrown by
 * hand — attack, damage, check, save, death save — and initiative and every NPC and enemy roll
 * stay Auto (§5.4; `rollModeFor` answers `auto` for any actor that is not the player's
 * character whatever this table says). ADR-061 had the four in Auto and only the death save
 * in the player's hand; what the founder decided from was neither — see the ADR.
 *
 * `concentration` is dead: nothing rolls under it. A concentration check is issued as a save
 * (`magic.ts`: `kind: 'save', key: 'con'`) and rolls as `save`; the entry is kept for the enum.
 */
export const DEFAULT_ROLL_MODES = Object.freeze({
    attack: 'player',
    check: 'player',
    save: 'player',
    death_save: 'player',
    damage: 'player',
    initiative: 'auto',
    concentration: 'auto',
});
/* ------------------------------------------------------------ world clocks */
/** The six world periods (architecture §4.2, §6.2). */
export const TimeOfDay = z.enum([
    'dawn',
    'morning',
    'afternoon',
    'evening',
    'night',
    'late_night',
]);
export const Weather = z.enum(['clear', 'rain', 'fog', 'snow']);
export const Lighting = z.enum(['bright', 'dim', 'dark']);
/* ---------------------------------------------------------------- fiction */
/** Engine modes mirrored by the client; each fixes mic policy and HUD (§1.2). */
export const GameMode = z.enum([
    'explore',
    'dialogue',
    'travel',
    'combat',
    'downtime',
    'awaiting_roll',
    'paused',
]);
/** Tension-peak kinds. A peak is never a scheduled fight (§3.5, §6.10). */
export const PeakKind = z.enum([
    'confrontation',
    'chase',
    'negotiation',
    'discovery',
    'horror',
    'dilemma',
    'combat',
]);
/** Relationship axes, clamped by the engine (§3.3 `apply_scene_outcome`). */
export const RelationshipAxis = z.enum(['trust', 'respect', 'affinity', 'fear']);
export const RelationshipDelta = z.object({
    trust: z.number().int().min(-20).max(20),
    respect: z.number().int().min(-20).max(20),
    affinity: z.number().int().min(-20).max(20),
    fear: z.number().int().min(-20).max(20),
});
/** The ten moods `apply_scene_outcome` may set on an NPC card. */
export const NpcMood = z.enum([
    'neutral',
    'pleased',
    'grateful',
    'amused',
    'impressed',
    'wary',
    'annoyed',
    'angry',
    'afraid',
    'sad',
]);
/** Rumor categories; `none` means "no rumor seeded by this turn". */
export const RumorCategory = z.enum([
    'none',
    'crime',
    'trade',
    'politics',
    'war',
    'romance',
    'supernatural',
    'personal',
]);
/** Where a fact on an entity card came from (§3.6). */
export const FactProvenance = z
    .string()
    .regex(/^(author|sim:[a-z0-9_:.-]+|gm:s\d+|player:flag)$/);
/* -------------------------------------------------------------- narration */
/** pocket-tts mood voice states (§7.1). */
export const VoiceMood = z.enum(['neutral', 'tense', 'whisper', 'jovial', 'grave']);
/** Deterministic tone tags extracted in the voice node (§3.7, §7.1). */
export const ToneTag = z.enum([
    'whisper',
    'quiet',
    'loud',
    'shout',
    'slow',
    'fast',
    'flat',
    'animated',
    'hesitant',
]);
/** Numeric result lines, or "cinematic" ones that keep numbers off the voice (§5.4). */
export const ResultLineStyle = z.enum(['numeric', 'cinematic']);
/* ------------------------------------------------------------------ errors */
/** Typed engine rejections (architecture §5.3). A rejected command emits no event. */
export const RulesErrorCode = z.enum([
    'NotYourTurn',
    'AwaitingRoll',
    'RollNotPending',
    'InsufficientMovement',
    'NoPath',
    'OutOfRange',
    'NoLineOfSight',
    'ActionAlreadyUsed',
    'BonusActionUnavailable',
    'ReactionUsed',
    'SlotUnavailable',
    'SpellNotPrepared',
    'ConcentrationConflict',
    'TargetInvalid',
    'UnknownRef',
    'PredicateFailed',
    'CapExceeded',
    'PreconditionFailed',
]);
export const RulesError = z.object({
    code: RulesErrorCode,
    /** i18n key for the player-facing HUD toast; the voice never speaks these. */
    messageKey: z.string(),
    slots: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
    /** One English line the model can act on, delivered as an `is_error` tool_result. */
    hint: z.string(),
});
/* -------------------------------------------------------------- geometry */
export const Cell = z.object({
    x: z.number().int().min(0),
    y: z.number().int().min(0),
});
/**
 * ADR-119: the unit every distance the table shows is said in. The rules are written in
 * feet and the engine keeps counting in them — this is presentation, and only that.
 *
 * Until 2026-09-18 the unit rode on the language: an English table read feet, a Portuguese
 * one metres, and neither could choose. A table anywhere may want either (founder,
 * 2026-09-18: "devemos poder alterar na metrica entre pes e metros, em toda a estrutura de
 * metrica do sistema que e exibido para o usuario").
 */
export const Measures = z.enum(['feet', 'metres']);
/** The grid's square, in each unit: the rules' five feet, and the metre and a half a table says. */
export const FEET_PER_SQUARE = 5;
export const METRES_PER_SQUARE = 1.5;
/** Feet as metres, at the table's rate rather than the surveyor's: a square is a square. */
export function metresFromFeet(feet) {
    return (feet / FEET_PER_SQUARE) * METRES_PER_SQUARE;
}
/**
 * A distance as the table reads it — the number and its unit, in the table's language.
 * Portuguese writes a comma where English writes a point, and neither shows a decimal it
 * does not need (`9 m`, not `9,0 m`).
 */
export function distanceLabel(feet, measures, locale = 'en-US') {
    if (measures === 'feet')
        return `${Math.round(feet)} ft`;
    const metres = metresFromFeet(feet);
    const written = Number.isInteger(metres) ? `${metres}` : metres.toFixed(1);
    return `${locale === 'pt-BR' ? written.replace('.', ',') : written} m`;
}
//# sourceMappingURL=primitives.js.map